import React, { useEffect, useMemo, useState } from "react";
import HRHeader from "../components/HRHeader";
import api from "../api";
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval,
  addMonths, subMonths, format, isSameMonth, isSameDay, parseISO, isWeekend,
} from "date-fns";
import { it } from "date-fns/locale";
import { ChevronLeft, ChevronRight, CalendarRange, Users } from "lucide-react";
import { TYPE_LABELS } from "./FeriePage";
import { SHIFT_LABELS, SHIFT_SHORT, SHIFT_COLORS, SHIFT_TYPES } from "../shifts";

const TYPE_COLORS = {
  ferie: "#16345c",
  permesso: "#e67e22",
  malattia: "#c0392b",
  permesso_104: "#8e44ad",
  lutto: "#6b7280",
  assente: "#94a3b8",
};

const typeLabel = (t) => TYPE_LABELS[t] || "Assente";

const WEEKDAYS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

const CalendarPage = () => {
  const [leaves, setLeaves] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [cursor, setCursor] = useState(new Date(2026, 7, 1)); // Aug 2026

  useEffect(() => {
    api.get("/leaves/calendar").then((r) => setLeaves(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const start = startOfWeek(startOfMonth(cursor), { weekStartsOn: 1 });
    const end = endOfWeek(endOfMonth(cursor), { weekStartsOn: 1 });
    api.get(`/shifts/calendar?start=${format(start, "yyyy-MM-dd")}&end=${format(end, "yyyy-MM-dd")}`)
      .then((r) => setShifts(r.data || [])).catch(() => {});
  }, [cursor]);

  const shiftsByDay = useMemo(() => {
    const map = {};
    (shifts || []).forEach((s) => { (map[s.date] = map[s.date] || []).push(s); });
    return map;
  }, [shifts]);

  // Map "yyyy-MM-dd" -> [events]
  const eventsByDay = useMemo(() => {
    const map = {};
    leaves.forEach((lv) => {
      try {
        const s = parseISO(lv.start_date);
        const e = parseISO(lv.end_date);
        const days = eachDayOfInterval({ start: s, end: e });
        days.forEach((d) => {
          if (lv.type === "ferie" && isWeekend(d)) return; // skip weekends for ferie
          const key = format(d, "yyyy-MM-dd");
          (map[key] = map[key] || []).push(lv);
        });
      } catch (err) {
        if (process.env.NODE_ENV !== "production") console.error("Errore parsing assenza nel calendario", err);
      }
    });
    return map;
  }, [leaves]);

  const gridDays = useMemo(() => {
    const start = startOfWeek(startOfMonth(cursor), { weekStartsOn: 1 });
    const end = endOfWeek(endOfMonth(cursor), { weekStartsOn: 1 });
    return eachDayOfInterval({ start, end });
  }, [cursor]);

  // Upcoming approved absences (sorted)
  const upcoming = useMemo(() => {
    return [...leaves]
      .sort((a, b) => a.start_date.localeCompare(b.start_date))
      .slice(0, 8);
  }, [leaves]);

  const monthLabel = format(cursor, "MMMM yyyy", { locale: it });

  return (
    <div className="min-h-screen bg-[#f0f2f5]">
      <HRHeader />
      <main className="max-w-[1200px] mx-auto px-4 py-8">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div>
            <h1 className="text-[26px] font-black text-[#16345c] mb-1 flex items-center gap-2">
              <CalendarRange size={24} /> Calendario Team
            </h1>
            <p className="text-[14px] text-gray-500">Assenze approvate di tutto il team</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setCursor(subMonths(cursor, 1))} className="w-9 h-9 flex items-center justify-center rounded-sm border border-gray-300 bg-white hover:bg-gray-50 transition-colors text-[#16345c]"><ChevronLeft size={18} /></button>
            <span className="text-[16px] font-bold text-[#16345c] capitalize min-w-[150px] text-center">{monthLabel}</span>
            <button onClick={() => setCursor(addMonths(cursor, 1))} className="w-9 h-9 flex items-center justify-center rounded-sm border border-gray-300 bg-white hover:bg-gray-50 transition-colors text-[#16345c]"><ChevronRight size={18} /></button>
            <button onClick={() => setCursor(new Date(2026, 7, 1))} className="ml-1 px-3 h-9 text-[13px] font-semibold rounded-sm border border-gray-300 bg-white hover:bg-gray-50 transition-colors text-[#16345c]">Oggi</button>
          </div>
        </div>

        {/* Legend */}
        <div className="flex flex-wrap items-center gap-4 mb-4">
          {Object.entries(TYPE_LABELS).map(([k, v]) => (
            <span key={k} className="flex items-center gap-1.5 text-[12px] text-gray-600">
              <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: TYPE_COLORS[k] }} /> {v}
            </span>
          ))}
          <span className="w-px h-4 bg-gray-300" />
          {SHIFT_TYPES.map((t) => (
            <span key={t} className="flex items-center gap-1.5 text-[12px] text-gray-600">
              <span className="w-3 h-3 rounded-sm border-l-2 border-current" style={{ color: SHIFT_COLORS[t], backgroundColor: `${SHIFT_COLORS[t]}33` }} /> Turno {SHIFT_LABELS[t]}
            </span>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Calendar grid */}
          <div className="lg:col-span-3 bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="grid grid-cols-7 bg-[#16345c] text-white">
              {WEEKDAYS.map((d) => (
                <div key={d} className="text-center py-2.5 text-[12px] font-bold uppercase tracking-wide">{d}</div>
              ))}
            </div>
            <div className="grid grid-cols-7">
              {gridDays.map((day) => {
                const key = format(day, "yyyy-MM-dd");
                const events = eventsByDay[key] || [];
                const inMonth = isSameMonth(day, cursor);
                const today = isSameDay(day, new Date(2026, 7, 13));
                return (
                  <div key={key} className={`min-h-[96px] border-b border-r border-gray-100 p-1.5 ${inMonth ? "bg-white" : "bg-gray-50/60"}`}>
                    <div className={`text-[12px] font-bold mb-1 flex items-center justify-center w-6 h-6 rounded-full ${today ? "bg-[#e30613] text-white" : inMonth ? "text-gray-700" : "text-gray-300"}`}>
                      {format(day, "d")}
                    </div>
                    <div className="space-y-1">
                      {events.slice(0, 3).map((ev, i) => (
                        <div key={ev.id + i} className="text-[10px] leading-tight text-white rounded px-1 py-0.5 truncate" style={{ backgroundColor: TYPE_COLORS[ev.type] || "#16345c" }} title={`${ev.employee_name} - ${typeLabel(ev.type)}`}>
                          {ev.employee_name.split(" ")[0]}
                        </div>
                      ))}
                      {events.length > 3 && (
                        <div className="text-[10px] text-gray-500 font-semibold pl-1">+{events.length - 3} altri</div>
                      )}
                      {(shiftsByDay[key] || []).slice(0, 2).map((sh, i) => (
                        <div key={sh.id + i} className="text-[10px] leading-tight rounded px-1 py-0.5 truncate flex items-center gap-1 border-l-2" style={{ borderColor: SHIFT_COLORS[sh.type], backgroundColor: `${SHIFT_COLORS[sh.type]}1a`, color: "#334155" }} title={`${sh.employee_name} · Turno ${SHIFT_LABELS[sh.type]}`} data-testid={`cal-shift-${key}`}>
                          <span className="font-black" style={{ color: SHIFT_COLORS[sh.type] }}>{SHIFT_SHORT[sh.type]}</span>
                          <span className="truncate">{sh.employee_name.split(" ")[0]}</span>
                        </div>
                      ))}
                      {(shiftsByDay[key] || []).length > 2 && (
                        <div className="text-[10px] text-gray-500 font-semibold pl-1">+{shiftsByDay[key].length - 2} turni</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Upcoming list */}
          <aside>
            <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
              <div className="px-4 py-3 bg-[#16345c] text-white text-[13px] font-bold uppercase tracking-wide flex items-center gap-2">
                <Users size={15} /> Prossime assenze
              </div>
              {upcoming.length === 0 ? (
                <div className="p-6 text-center text-gray-400 text-[13px]">Nessuna assenza approvata</div>
              ) : (
                upcoming.map((lv, i) => (
                  <div key={lv.id + i} className="px-4 py-3 border-b border-gray-100 last:border-0 flex items-start gap-3">
                    <span className="w-2.5 h-2.5 rounded-full mt-1.5 shrink-0" style={{ backgroundColor: TYPE_COLORS[lv.type] || "#16345c" }} />
                    <div className="min-w-0">
                      <div className="text-[13px] font-bold text-[#16345c]">{lv.employee_name}</div>
                      <div className="text-[12px] text-gray-500">{typeLabel(lv.type)}</div>
                      <div className="text-[11.5px] text-gray-400">
                        {lv.type === "permesso"
                          ? `${format(parseISO(lv.start_date), "dd MMM", { locale: it })} · ${lv.hours}h`
                          : `${format(parseISO(lv.start_date), "dd MMM", { locale: it })} → ${format(parseISO(lv.end_date), "dd MMM", { locale: it })}`}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
};

export default CalendarPage;
